import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { Accordion, Card, Spinner } from 'react-bootstrap';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import { MultiSelect } from 'react-multi-select-component';
import Loader from '../Utility/Loader/Loader';
import CustomTooltip from '../Tooltip/CustomTooltip';

function SystemMasterTabel({
  isLoading,
  setShow,
  callApi,
  buttonDown,
  mainData,
  category1List = [],
  handleChanged,
  addBtnClicked,
  deletPressed,
  onSave,
  isDesabled,
  title,
  masterAddEditAccess,
  masterDeleteAccess,
  inputName,
  activeInactiveClicked,
  btnDisable,
  isUnique,
  isUniqueId,
  allowedIds,
  addLimit,
  nameMaxLength = 30,
  OpenReArrangeFunction,
  showAlias,
  userRole,
  handleCheckboxChangeLength,
  isLengthVisible,
  isLengthToggleLocked = false,
  lengthToggleLockedReason = '',
  editableName,
  onEditableNameChange,
  onEditableNameSave,
  isSavingName,
  productTypeOptions = [],
  onChangeProductTypes,
  placeholder,
  catBasedOnPTData,
}) {
  const { t } = useTranslation('common');
  const [selectedCategory1, setSelectedCategory1] = useState(null);
  const CATEGORY_1 = 'Category 1';
  const CATEGORY_2 = 'Category 2';

  /**
   * Builds the list of Tier1 active product-type options normalised into MultiSelect
   * label/value shape, recomputed only when the source product-type list changes.
   */
  const transformedProductTypeOptions = useMemo(() => {
    return (
      productTypeOptions
        ?.filter((option) => option?.tier === 'Tier1' && option?.isActive === true)
        ?.map((option) => ({
          label: option?.name || '',
          value: option?._id || option?.id || '',
          tier: option?.tier || '',
          ...option,
        })) || []
    );
  }, [productTypeOptions]);

  /**
   * Normalises a row's selected product types into MultiSelect label/value objects,
   * accepting either already-shaped option objects or a plain array of ids.
   * @param {Array} selectedTypes - Currently selected product types (objects or ids).
   * @returns {Array} Array of { label, value } option objects.
   */
  const formatSelectedValues = useCallback(
    (selectedTypes) => {
      if (!selectedTypes || !Array.isArray(selectedTypes)) {
        return [];
      }
      if (selectedTypes.length > 0 && selectedTypes[0]?.label && selectedTypes[0]?.value) {
        return selectedTypes;
      }
      return (
        selectedTypes
          ?.map((selectedId) => {
            const matchedOption = transformedProductTypeOptions.find((option) => option?.value === selectedId || option?._id === selectedId);
            return matchedOption ? { label: matchedOption?.label, value: matchedOption?.value } : null;
          })
          ?.filter(Boolean) || []
      );
    },
    [transformedProductTypeOptions],
  );

  /**
   * Builds product-type options for a specific row, marking already-used product-type
   * ids as disabled so they cannot be deselected.
   * @param {Object} item - The row whose used product-type ids gate the options.
   * @returns {Array} Product-type options with a `disabled` flag per option.
   */
  const buildProductTypeOptions = useCallback(
    (item) => {
      const usedPTIds = Array.isArray(item?.isUsedProductType) ? item?.isUsedProductType : [];
      return (transformedProductTypeOptions || []).map((opt) => ({
        ...opt,
        disabled: usedPTIds.includes(opt?.value),
      }));
    },
    [transformedProductTypeOptions],
  );

  /**
   * Builds Category 1 options for a specific Category 2 row, marking already-used
   * Category 1 ids as disabled.
   * @param {Object} item - The Category 2 row whose used Cat1 ids gate the options.
   * @returns {Array} Category 1 options with a `disabled` flag per option.
   */
  const buildCategory1Options = useCallback(
    (item) => {
      const usedIds = item?.isUsedCat1 || [];
      return (category1List || []).map((catItem) => ({
        label: catItem?.name,
        value: catItem?._id,
        disabled: Array.isArray(usedIds) ? usedIds.includes(catItem?._id) : false,
      }));
    },
    [category1List],
  );

  /**
   * Maps an array of Category 1 ids to MultiSelect label/value options using the
   * available Category 1 list, dropping ids without a match.
   * @param {Array} ids - Category 1 ids to resolve.
   * @returns {Array} Array of { label, value } option objects.
   */
  const mapCatOneIdsToOptions = useCallback(
    (ids = []) => {
      if (!Array.isArray(ids)) return [];
      return ids
        .map((id) => {
          const match = (category1List || []).find((c1) => c1?._id === id);
          return match ? { label: match?.name, value: match?._id } : null;
        })
        .filter(Boolean);
    },
    [category1List],
  );

  /**
   * Returns the icon class for a row's active/inactive status indicator.
   * @param {Object} item - The row whose active state selects the icon.
   * @returns {string} Icon class string for the status indicator.
   */
  const activeClass = (item) => {
    return item.isActive ? 'icon-active f-24' : 'icon-inactive f-24';
  };
  const btnIcon = useMemo(
    () => (!buttonDown ? <i className="icon-new-down-arrow-icon icon-inverse f-24" /> : <i className="icon-new-down-arrow-icon f-24" />),
    [buttonDown],
  );
  /**
   * Handles the add-row action, resetting the selected Category 1 filter for the
   * Category 2 master before delegating to the parent add handler.
   */
  const handleAddBtnClicked = () => {
    if (title === CATEGORY_2) {
      setSelectedCategory1(null);
    }
    addBtnClicked();
  };

  /**
   * Renders the Length master's visibility switch, wrapped in a tooltip explaining the lock when
   * downstream modules already store a length and the switch may therefore not be turned off.
   */
  /**
   * The Lengths visibility toggle, paired with the editable Variable 1 name (BM-9583).
   *
   * There used to be two toggles for this one setting — a static one labelled "Lengths" and this
   * one — both bound to `isLengthVisible` and both using `id="design"`, so clicking either label
   * was ambiguous. Only the static one carried the in-use lock. They are merged here: one control,
   * a unique id, and the lock preserved.
   * @returns The toggle, wrapped in its locked-reason tooltip when the lock applies.
   */
  const renderLengthToggle = () => {
    const lengthToggle = (
      <div className="custom-control pl-0 d-flex align-items-center design-switch pt-0">
        <input
          type="checkbox"
          className="form-check-input"
          id="lengthVisibleToggle"
          name="lengthVisibleToggle"
          checked={isLengthVisible}
          disabled={userRole !== 'Admin' || isLengthToggleLocked}
          onChange={(e) => handleCheckboxChangeLength(e)}
        />
        <label className="design-title mb-0" htmlFor="lengthVisibleToggle">
          <span className="sr-only">{title}</span>
        </label>
        <div className="length-name-edit d-flex align-items-center">
          <input
            type="text"
            className="form-control name-text text-black text-bold f-18"
            value={editableName ?? ''}
            maxLength={30}
            disabled={userRole !== 'Admin' || !masterAddEditAccess}
            aria-label={t('systemMaster:LENGTH_MASTER.EDIT_NAME_LABEL')}
            onChange={(e) => onEditableNameChange?.(e)}
            onBlur={() => onEditableNameSave?.()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.currentTarget.blur();
              }
            }}
          />
          {isSavingName && <Spinner animation="border" size="sm" className="ml-2" />}
        </div>
      </div>
    );

    if (isLengthToggleLocked && !isEmpty(lengthToggleLockedReason)) {
      return <CustomTooltip tooltipText={lengthToggleLockedReason}>{lengthToggle}</CustomTooltip>;
    }
    return lengthToggle;
  };

  // Helper to render list item content without nested ternary
  const renderItemContent = (item, i) => {
    const lengthActionsDisabled = inputName === 'lengths' && (userRole !== 'Admin' || !masterAddEditAccess);
    if (catBasedOnPTData && title === CATEGORY_2) {
      return (
        <div className="mb-2 flex-fill max-w-100">
          <div className="common-react-select mb-2">
            <MultiSelect
              options={buildCategory1Options(item)}
              value={mapCatOneIdsToOptions(item?.catOneIds)}
              onChange={(selected) => {
                const values = Array.isArray(selected) ? selected.map((opt) => opt?.value) : [];
                const lockedIds = Array.isArray(item?.isUsedCat1) ? item?.isUsedCat1 : [];
                const merged = Array.from(new Set([...(values || []), ...lockedIds]));
                handleChanged(
                  {
                    target: {
                      name: 'catOneIds',
                      value: merged,
                    },
                  },
                  i,
                );
              }}
              overrideStrings={{
                selectSomeItems: 'Select Category 1',
                search: 'Search Category 1',
              }}
              labelledBy="Select Category 1"
              className="product-custom-multiselect common-new-multiselect w-100"
            />
            {item?.isEmpty && catBasedOnPTData && (!Array.isArray(item?.catOneIds) || item?.catOneIds?.length === 0) && (
              <label className="text-danger m-0 p-1">{t('SYSTEM_MASTER_TABLE.SELECT_CATEGORY1_REQUIRED', 'Please select Category 1')}</label>
            )}
          </div>
          <div className="d-flex align-items-center">
            {showAlias && <input name="aliasName" value={item?.aliasName} maxLength={5} onChange={(e) => handleChanged(e, i)} className="w-50px mr-2" />}
            <div className="input-container flex-grow-1">
              <div className="d-flex align-items-center">
                <input
                  id={`category2-${item?._id || 'new'}`}
                  key={item?._id}
                  maxLength={addLimit ? nameMaxLength : undefined}
                  value={item?.name || ''}
                  type="text"
                  onChange={(e) => handleChanged(e, i)}
                  disabled={!masterAddEditAccess || (!masterAddEditAccess && allowedIds && !isEmpty(allowedIds) && !allowedIds.includes(item?._id))}
                  className="form-control"
                />
                <div className="input-action d-flex align-items-center">
                  <button type="button" className="btn p-0 ml-2" aria-label="View">
                    <i className="icon-Eye f-20" />
                  </button>
                  {item?._id && masterAddEditAccess && (
                    <CustomTooltip tooltipText={item?.isActive ? 'Active' : 'Inactive'}>
                      <button onClick={() => activeInactiveClicked(item?._id, inputName)} type="button" aria-label="Active/Inactive">
                        <span className={activeClass(item)} />
                      </button>
                    </CustomTooltip>
                  )}
                  {masterDeleteAccess && (
                    <button type="button" className="btn p-0 ml-2" onClick={() => deletPressed(item._id ? item._id : i, inputName)} aria-label="Delete">
                      <i className="icon-Delete f-20" />
                    </button>
                  )}
                </div>
              </div>
              {!isEmpty(isUniqueId) && isUniqueId?.includes(i) && (
                <label className="text-danger m-0 p-1">{t('SYSTEM_MASTER_TABLE.THIS_ENTRY_ALREADY_EXISTS')}</label>
              )}
              {item?.isEmpty && (item?.name === null || item?.name === '') && (
                <label className="text-danger m-0 p-1">
                  {title} {t('SYSTEM_MASTER_TABLE.ENTRY_REQUIRED')}
                </label>
              )}
            </div>
          </div>
        </div>
      );
    }

    if (catBasedOnPTData && title === CATEGORY_1) {
      return (
        <div className="mb-2 flex-fill">
          <div className="common-react-select mb-2">
            <MultiSelect
              name={`productTypes_${i}`}
              options={buildProductTypeOptions(item)}
              value={formatSelectedValues(item?.productType || [])}
              className="product-custom-multiselect common-new-multiselect w-100"
              overrideStrings={{
                selectSomeItems: 'Select Product Type',
                search: 'Search Product Type',
              }}
              onChange={(selected) => {
                let selectedNorm = [];
                if (Array.isArray(selected)) {
                  selectedNorm = selected.map((opt) => ({ label: opt?.label, value: opt?.value }));
                }
                const lockedPTIds = Array.isArray(item?.isUsedProductType) ? item?.isUsedProductType : [];
                const lockedPTOptions = lockedPTIds
                  .map((id) => {
                    const match = (transformedProductTypeOptions || []).find((o) => o?.value === id);
                    return match ? { label: match?.label, value: match?.value } : { label: '', value: id };
                  })
                  .filter((v) => v?.value);
                const mergedByValue = [...selectedNorm, ...lockedPTOptions].reduce((acc, curr) => {
                  if (!acc.some((x) => x?.value === curr?.value)) acc.push(curr);
                  return acc;
                }, []);
                onChangeProductTypes?.(mergedByValue, i);
              }}
            />
            {item?.isEmpty && (!item?.productType || item?.productType?.length === 0) && (
              <label className="text-danger m-0 p-1">Please select product type</label>
            )}
          </div>
          <div className="d-flex align-items-center">
            {showAlias && <input name="aliasName" value={item?.aliasName} maxLength={5} onChange={(e) => handleChanged(e, i)} className="w-50px mr-2" />}
            <div className="input-container flex-grow-1">
              <input
                key={item?._id}
                maxLength={addLimit && nameMaxLength}
                value={item?.name || typeof item?.name === 'number' ? item?.name : ''}
                type="text"
                placeholder={placeholder}
                onChange={(e) => handleChanged(e, i)}
                disabled={!masterAddEditAccess || (!masterAddEditAccess && allowedIds && !isEmpty(allowedIds) && !allowedIds.includes(item?._id))}
              />
              {!isEmpty(isUniqueId) && isUniqueId?.includes(i) && (
                <label htmlFor={item?._id} className="text-danger m-0 p-1">
                  {t('SYSTEM_MASTER_TABLE.THIS_ENTRY_ALREADY_EXISTS')}
                </label>
              )}
              {item?.isEmpty && (item?.name === null || item?.name === '') && (
                <label htmlFor={item?._id} className="text-danger m-0 p-1">
                  {title} {t('SYSTEM_MASTER_TABLE.ENTRY_REQUIRED')}
                </label>
              )}
              {item?.isNew && !isEmpty(item?.name) && isEmpty(item?.name?.trim()) && (
                <label htmlFor={item?._id} className="text-danger m-0 p-1">
                  {title} {t('SYSTEM_MASTER_TABLE.ENTRY_IS_INVALID')}
                </label>
              )}
            </div>
            <div className="input-action d-flex align-items-center">
              {item?._id && masterAddEditAccess && (
                <CustomTooltip tooltipText={item?.isActive ? 'Active' : 'Inactive'}>
                  <button onClick={() => activeInactiveClicked(item?._id, inputName)} type="button" aria-label="Active/Inactive">
                    <span className={activeClass(item)} />
                  </button>
                </CustomTooltip>
              )}
              {masterDeleteAccess && (
                <CustomTooltip tooltipText="Delete">
                  <button type="button" onClick={() => deletPressed(item._id ? item._id : i, inputName)} aria-label="Delete">
                    <span className="icon-Delete f-24" />
                  </button>
                </CustomTooltip>
              )}
            </div>
          </div>
        </div>
      );
    }

    return (
      <>
        {showAlias && <input name="aliasName" value={item?.aliasName} maxLength={5} onChange={(e) => handleChanged(e, i)} className="w-50px mr-2" />}
        <div className="input-container">
          <input
            key={item?._id}
            maxLength={addLimit && nameMaxLength}
            value={item?.name || typeof item?.name === 'number' ? item?.name : ''}
            type="text"
            onChange={(e) => handleChanged(e, i)}
            disabled={!masterAddEditAccess || (!masterAddEditAccess && allowedIds && !isEmpty(allowedIds) && !allowedIds.includes(item?._id))}
          />
          {!isEmpty(isUniqueId) && isUniqueId?.includes(i) && (
            <label htmlFor={item?._id} className="text-danger m-0 p-1">
              {t('SYSTEM_MASTER_TABLE.THIS_ENTRY_ALREADY_EXISTS')}
            </label>
          )}
          {item?.isEmpty && (item?.name === null || item?.name === '') && (
            <label htmlFor={item?._id} className="text-danger m-0 p-1">
              {title} {t('SYSTEM_MASTER_TABLE.ENTRY_REQUIRED')}
            </label>
          )}
          {item?.isNew && !isEmpty(item?.name) && isEmpty(item?.name?.trim()) && (
            <label htmlFor={item?._id} className="text-danger m-0 p-1">
              {title} {t('SYSTEM_MASTER_TABLE.ENTRY_IS_INVALID')}
            </label>
          )}
        </div>
        <div className="input-action">
          {title === 'Brands' && (
            <>
              <label htmlFor="brands" className="m-0">
                {t('SYSTEM_MASTER_TABLE.ROYALTY_FEE')}
              </label>
              <div className="royaltyDiv">
                <input
                  key={item?._id}
                  name="royaltyFee"
                  value={item?.royaltyFee ? item?.royaltyFee : ''}
                  type="number"
                  onChange={(e) => handleChanged(e, i)}
                  disabled={!masterAddEditAccess || (!masterAddEditAccess && allowedIds && !isEmpty(allowedIds) && !allowedIds?.includes(item?._id))}
                />
                <span>%</span>
              </div>
            </>
          )}
          {item?._id !== '' && (masterAddEditAccess || inputName === 'lengths') && (
            <CustomTooltip tooltipText={t('SYSTEM_MASTER_TABLE.ACTIVE/INACTIVE')}>
              <button
                onClick={() => activeInactiveClicked(item?._id, inputName)}
                id={item._id}
                type="button"
                aria-label="Active/Inactive"
                disabled={
                  lengthActionsDisabled ||
                  !masterAddEditAccess ||
                  (!masterAddEditAccess && allowedIds && !isEmpty(allowedIds) && !allowedIds?.includes(item?._id))
                }
              >
                <span className={activeClass(item)} />
              </button>
            </CustomTooltip>
          )}
          {(masterDeleteAccess || inputName === 'lengths') && (
            <CustomTooltip tooltipText={t('SYSTEM_MASTER_TABLE.DELETE')}>
              <button
                type="button"
                onClick={() => {
                  deletPressed(item._id ? item._id : i, inputName);
                }}
                disabled={lengthActionsDisabled || (allowedIds && !isEmpty(allowedIds) && !allowedIds?.includes(item?._id))}
                aria-label="Delete"
              >
                <span className="icon-Delete f-24" />
              </button>
            </CustomTooltip>
          )}
        </div>
      </>
    );
  };
  return (
    <Accordion className="mb-4 master-accordian">
      <Card>
        <Card.Header>
          <div className="title-container">{inputName === 'lengths' ? renderLengthToggle() : <p>{title}</p>}</div>
          <div className={!masterAddEditAccess ? 'action-title-btns single-btn' : 'action-title-btns'}>
            {masterAddEditAccess && title === `${t('TEST_TYPE_MASTER.TITLE')}` && (
              <button
                type="button"
                onClick={() => OpenReArrangeFunction('testTypeMaster')}
                className="filter-btn filter-button_add"
                disabled={false}
                aria-label={t('PRODUCT_IDENTIFIER.REARRANGE')}
                title={t('PRODUCT_IDENTIFIER.REARRANGE')}
              >
                <span className="icon-expand" />
              </button>
            )}

            <CustomTooltip tooltipText={!buttonDown ? t('SYSTEM_MASTER_TABLE.CLOSE') : t('SYSTEM_MASTER_TABLE.OPEN')}>
              <button className="btn d-inline-flex p-0" onClick={() => callApi()} type="button">
                {btnIcon}
              </button>
            </CustomTooltip>
          </div>
        </Card.Header>
        {setShow && (
          <Card.Body>
            {isLoading ? (
              <Loader />
            ) : (
              <div className="common-scrollbar">
                <ul>{!isEmpty(mainData) && mainData.map((item, i) => <li key={item?._id || item?._cid || `row-${i}`}>{renderItemContent(item, i)}</li>)}</ul>
              </div>
            )}
            {masterAddEditAccess && (
              <CustomTooltip tooltipText={t('CUSTOM_ATTRIBUTES.ADD_TOOLTIP')}>
                <button
                  type="button"
                  onClick={catBasedOnPTData && title === 'Category 2' ? handleAddBtnClicked : addBtnClicked}
                  className="btn btn-outline ml-auto mr-2 mt-16 p-0"
                  disabled={btnDisable || !isUnique}
                  aria-label="Add"
                >
                  <span className="icon-Add text-grey  f-24" />
                </button>
              </CustomTooltip>
            )}
            {masterAddEditAccess ? (
              <div className="section-custom-save">
                <button aria-label="Save" type="button" className="btn mr-2 p-0" onClick={onSave} disabled={btnDisable || !isUnique}>
                  {isDesabled ? <Spinner animation="border" /> : <i className="icon-Tick-1 f-24" />}
                </button>
              </div>
            ) : (
              ''
            )}
          </Card.Body>
        )}
      </Card>
    </Accordion>
  );
}

export default SystemMasterTabel;
